import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faFilter, 
  faSortAmountDown, 
  faSortAmountUp, 
  faUser,
  faTruck,
  faTag,
  faSpinner,
  faEye,
  faPrint,
  faCalendarAlt,
  faTimesCircle,
  faPlus,
  faEdit,
  faTrash,
  faArchive,
  faEllipsisV
} from '@fortawesome/free-solid-svg-icons';
import { 
  getOrders, 
  getOrdersPaginated, 
  updateOrderStatus,
  updateOrderArchiveStatus,
  searchOrders,
  deleteOrder
} from '../../services/orderService';
import OrderDetails from './OrderDetails';
import ManualOrderForm from './ManualOrderForm';
import { formatDateSafe, formatDateShort } from '../../utils/dateUtils';

// DEBUG: Uncomment the line below to enable Firebase image debugging
// import ImageDebugComponent from '../debug/ImageDebugComponent';

const OrdersSection = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [archivedFilter, setArchivedFilter] = useState('active'); // 'active', 'archived', 'all'
  const [sortBy, setSortBy] = useState('delivery-asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage, setOrdersPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedOrders, setSelectedOrders] = useState({});
  const [selectMode, setSelectMode] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);
  const [showManualOrderForm, setShowManualOrderForm] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  // Row "Actions" kebab dropdown: the open order + where to anchor the menu
  const [actionMenuOrder, setActionMenuOrder] = useState(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, right: 0 });

  // Fetch orders on component mount
  useEffect(() => {
    fetchOrders();
  }, []);

  // Open/close the per-row actions menu, anchored to the clicked kebab button
  const toggleActionMenu = (e, order) => {
    e.stopPropagation();
    if (actionMenuOrder && actionMenuOrder.id === order.id) {
      setActionMenuOrder(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setActionMenuPos({
      top: rect.bottom + 6,
      right: Math.max(8, window.innerWidth - rect.right)
    });
    setActionMenuOrder(order);
  };

  // Close the actions menu on outside click, scroll, resize, or Escape
  useEffect(() => {
    if (!actionMenuOrder) return;
    const close = () => setActionMenuOrder(null);
    const onPointerDown = (e) => {
      if (!e.target.closest('.action-menu-dropdown') && !e.target.closest('.action-menu-trigger')) {
        close();
      }
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [actionMenuOrder]);

  // Reset to first page when ordersPerPage changes
  useEffect(() => {
    if (orders.length > 0) { // Only reset if we already have orders
      setCurrentPage(1);
    }
  }, [ordersPerPage]);
  
  // Fetch orders from Firebase
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const allOrders = await getOrders(); // Get ALL orders instead of paginated

      setOrders(allOrders);
      setFilteredOrders(allOrders);
      setLastVisible(null); // Not needed for client-side pagination
      
      setError(null);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  

  
  // Apply filters and sorting
  useEffect(() => {
    let result = [...orders];
    
    // Apply archived filter first (most important for reducing clutter)
    if (archivedFilter === 'active') {
      result = result.filter(order => !order.archived);
    } else if (archivedFilter === 'archived') {
      result = result.filter(order => order.archived);
    }
    // 'all' shows both archived and active
    
    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(order =>
        (order.status || '').toLowerCase() === statusFilter.toLowerCase()
      );
    }
    
    // Apply location filter
    if (locationFilter !== 'all') {
      result = result.filter(order => {
        const orderLocation = order.location || order.delivery?.city || order.deliveryInfo?.city || 'Unknown';
        return orderLocation.toLowerCase() === locationFilter.toLowerCase();
      });
    }
    
    // Apply search filter
    if (searchTerm) {
      result = result.filter(order => 
        order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customer?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply date filter (for delivery dates)
    if (startDate && endDate) {
      result = result.filter(order => {
        const deliveryDate = order.rentalDate || 
                             order.deliveryDate || 
                             order.delivery?.date || 
                             order.deliveryInfo?.deliveryDate ||
                             order.deliveryInfo?.startDate ||
                             order.createdAt;
        return new Date(deliveryDate) >= new Date(startDate) && new Date(deliveryDate) <= new Date(endDate);
      });
    }
    
    // Apply sorting with delivery date priority
    result.sort((a, b) => {
      const getDeliveryDate = (order) => order.rentalDate || 
                                        order.deliveryDate || 
                                        order.delivery?.date || 
                                        order.deliveryInfo?.deliveryDate ||
                                        order.deliveryInfo?.startDate ||
                                        order.createdAt;
      const getOrderDate = (order) => order.createdAt;
      const getLocation = (order) => order.location || order.delivery?.city || order.deliveryInfo?.city || 'Unknown';
      
      switch (sortBy) {
        case 'delivery-asc':
          return new Date(getDeliveryDate(a)) - new Date(getDeliveryDate(b));
        case 'delivery-desc':
          return new Date(getDeliveryDate(b)) - new Date(getDeliveryDate(a));
        case 'inventory-angola':
          // Angola inventory first, then by delivery date
          const aSource = getInventorySource(a);
          const bSource = getInventorySource(b);
          if (aSource.includes('Angola') && !bSource.includes('Angola')) return -1;
          if (!aSource.includes('Angola') && bSource.includes('Angola')) return 1;
          return new Date(getDeliveryDate(a)) - new Date(getDeliveryDate(b));
        case 'inventory-elkhart':
          // Elkhart inventory first, then by delivery date
          const aSourceE = getInventorySource(a);
          const bSourceE = getInventorySource(b);
          if (aSourceE.includes('Elkhart') && !bSourceE.includes('Elkhart')) return -1;
          if (!aSourceE.includes('Elkhart') && bSourceE.includes('Elkhart')) return 1;
          return new Date(getDeliveryDate(a)) - new Date(getDeliveryDate(b));
        case 'location-asc':
          return getLocation(a).localeCompare(getLocation(b));
        case 'location-desc':
          return getLocation(b).localeCompare(getLocation(a));
        case 'date-asc':
          return new Date(getOrderDate(a)) - new Date(getOrderDate(b));
        case 'date-desc':
          return new Date(getOrderDate(b)) - new Date(getOrderDate(a));
        case 'total-asc':
          return (a.total || a.totalAmount || 0) - (b.total || b.totalAmount || 0);
        case 'total-desc':
          return (b.total || b.totalAmount || 0) - (a.total || a.totalAmount || 0);
        default:
          return 0;
      }
    });
    
    setFilteredOrders(result);
  }, [orders, statusFilter, searchTerm, sortBy, startDate, endDate, locationFilter, archivedFilter]);
  
  // Update total pages when filtered orders or orders per page changes
  useEffect(() => {
    const totalPagesCalc = Math.ceil(filteredOrders.length / ordersPerPage);
    setTotalPages(totalPagesCalc);
  }, [filteredOrders.length, ordersPerPage]);

  // Handle current page validation separately
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);
  
  // Handle search with debounce
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  
  // Use centralized date utility for consistent timezone-safe formatting
  const formatDate = (date) => {
    return formatDateShort(date);
  };
  
  // Get status class for styling
  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'status-completed';
      case 'processing':
        return 'status-processing';
      case 'pending':
        return 'status-pending';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return '';
    }
  };
  
  // Handle viewing order details
  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };
  
  // Handle updating order status
  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      setIsUpdatingStatus(orderId);
      await updateOrderStatus(orderId, newStatus);
      
      // Update the order in the local state
      const updatedOrders = orders.map(order => 
        order.id === orderId ? { ...order, status: newStatus } : order
      );
      
      setOrders(updatedOrders);
      
      // Show success message
      alert(`Order status updated to ${newStatus}`);
    } catch (err) {
      console.error('Error updating order status:', err);
      alert('Failed to update order status. Please try again.');
    } finally {
      setIsUpdatingStatus(null);
    }
  };
  
  // Handle status update menu
  const handleStatusMenu = (e, orderId, currentStatus) => {
    e.stopPropagation();
    
    const statusOptions = ['Pending', 'Processing', 'Completed', 'Cancelled'];
    const currentIndex = statusOptions.findIndex(status => 
      status.toLowerCase() === currentStatus.toLowerCase()
    );
    
    // Create a dropdown menu
    const menu = document.createElement('div');
    menu.className = 'status-dropdown';
    
    statusOptions.forEach(status => {
      const option = document.createElement('div');
      option.className = `status-option ${status.toLowerCase() === currentStatus.toLowerCase() ? 'active' : ''}`;
      option.innerText = status;
      option.onclick = () => {
        if (status.toLowerCase() !== currentStatus.toLowerCase()) {
          handleUpdateStatus(orderId, status);
        }
        document.body.removeChild(menu);
      };
      menu.appendChild(option);
    });
    
    // Position and show the menu
    const button = e.currentTarget;
    const rect = button.getBoundingClientRect();
    menu.style.position = 'absolute';
    menu.style.top = `${rect.bottom + window.scrollY}px`;
    menu.style.left = `${rect.left + window.scrollX}px`;
    menu.style.zIndex = 1000;
    
    // Add click outside handler
    const handleClickOutside = (event) => {
      if (!menu.contains(event.target) && event.target !== button) {
        document.body.removeChild(menu);
        document.removeEventListener('click', handleClickOutside);
      }
    };
    
    document.body.appendChild(menu);
    setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);
  };
  
  // Handle generating invoice
  const handleGenerateInvoice = (order) => {
    // Use the comprehensive invoice generator from OrderDetails
    // This opens the order details modal which has the full PDF generation
    handleViewOrder(order);
  };
  
  // Handle closing order details
  const handleCloseOrderDetails = () => {
    setShowOrderDetails(false);
    setSelectedOrder(null);
  };
  
  // Function to handle printing filtered orders
  const handlePrintOrders = () => {
    // Get orders to print - either selected orders or all filtered orders
    const ordersToPrint = getSelectedOrdersCount() > 0 
      ? getSelectedOrdersArray() 
      : filteredOrders;
    
    // If no orders to print, show alert
    if (ordersToPrint.length === 0) {
      alert('No orders selected for printing.');
      return;
    }
    
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    // Get current date for the report header
    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    // Create the print content with styling
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Rowe Bros - Delivery Orders</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            line-height: 1.5;
          }
          .header {
            text-align: center;
            margin-bottom: 20px;
            border-bottom: 2px solid #333;
            padding-bottom: 10px;
          }
          .header h1 {
            margin: 0;
            color: #333;
          }
          .header p {
            margin: 5px 0;
            color: #666;
          }
          .filter-info {
            margin-bottom: 20px;
            font-style: italic;
            color: #666;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }
          th {
            background-color: #f2f2f2;
            font-weight: bold;
          }
          tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          .order-status {
            padding: 3px 8px;
            border-radius: 12px;
            font-size: 0.85em;
            display: inline-block;
          }
          .status-pending {
            background-color: #ffeeba;
            color: #856404;
          }
          .status-processing {
            background-color: #b8daff;
            color: #004085;
          }
          .status-completed {
            background-color: #c3e6cb;
            color: #155724;
          }
          .status-cancelled {
            background-color: #f5c6cb;
            color: #721c24;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 0.8em;
            color: #666;
          }
          .delivery-info {
            margin-top: 5px;
            font-size: 0.9em;
            color: #666;
          }
          .payment-collect {
            background-color: #fff3e0 !important;
            color: #ef6c00 !important;
            font-weight: bold;
            border: 2px solid #ff9800;
          }
          .payment-paid {
            background-color: #e8f5e9 !important;
            color: #2e7d32 !important;
            font-weight: bold;
            border: 2px solid #4caf50;
          }
          .payment-deposit {
            background-color: #e3f2fd !important;
            color: #1976d2 !important;
            font-weight: bold;
            border: 2px solid #2196f3;
          }
          @media print {
            .no-print {
              display: none;
            }
            .payment-collect {
              background-color: #f0f0f0 !important;
              color: #000 !important;
              border: 2px solid #000 !important;
            }
            .payment-paid {
              background-color: #f0f0f0 !important;
              color: #000 !important;
              border: 1px solid #666 !important;
            }
            .payment-deposit {
              background-color: #f0f0f0 !important;
              color: #000 !important;
              border: 1px solid #000 !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Rowe Bros Delivery Orders</h1>
          <p>${formattedDate}</p>
        </div>
        
        <div class="filter-info">
          ${getSelectedOrdersCount() > 0 
            ? `${getSelectedOrdersCount()} selected orders` 
            : startDate && endDate 
              ? `Orders from ${formatDateShort(startDate)} to ${formatDateShort(endDate)}` 
              : 'All Orders'}
          ${statusFilter !== 'all' ? ` | Status: ${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}` : ''}
          ${archivedFilter !== 'all' ? ` | ${archivedFilter === 'active' ? 'Active Only' : 'Archived Only'}` : ''}
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Customer</th>
              <th>Date</th>
              <th>Address</th>
              <th>Delivery Time</th>
              <th>Items</th>
              <th>Total</th>
              <th>💸 Payment Due</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${ordersToPrint.map(order => `
              <tr>
                <td>${order.id.substring(0, 8)}...</td>
                <td>
                  ${order.customerName || 
                    (order.customer?.name) || 
                    (order.customerInfo?.name) || 
                    (order.customerInfo?.firstName && order.customerInfo?.lastName ? 
                      `${order.customerInfo.firstName} ${order.customerInfo.lastName}` : 
                      (order.customer?.firstName && order.customer?.lastName ? 
                        `${order.customer.firstName} ${order.customer.lastName}` : 
                        'Guest'))}
                  <div>${order.customer?.phone || order.customerInfo?.phone || ''}</div>
                </td>
                <td>${formatDateShort(order.createdAt)}</td>
                <td>
                  ${order.delivery?.address || order.deliveryInfo?.address || 'N/A'}<br>
                  ${(order.delivery?.city || order.deliveryInfo?.city) ? 
                    `${order.delivery?.city || order.deliveryInfo?.city}, 
                     ${order.delivery?.state || order.deliveryInfo?.state} 
                     ${order.delivery?.zipCode || order.deliveryInfo?.zipCode}` : 'N/A'}
                </td>
                <td>
                  ${order.delivery?.date || order.deliveryInfo?.deliveryDate || 'N/A'}<br>
                  ${order.delivery?.time || order.deliveryInfo?.deliveryTime || 'N/A'}
                </td>
                <td>
                  ${order.items.map(item => `
                    ${item.quantity || 1} x ${item.name} ($${(item.price || 0).toFixed(2)})
                  `).join('<br>')}
                </td>
                <td>$${(order.total || order.totalAmount || 0).toFixed(2)}</td>
                <td style="font-weight: bold;">
                  ${(() => {
                    // Enhanced payment status logic with better validation
                    const paymentInfo = order.paymentInfo || {};
                    const paymentOption = order.paymentOption || paymentInfo.paymentOption;
                    const isTestOrder = paymentInfo.isTestOrder || order.isTestOrder || false;
                    
                    // Get amounts with proper fallbacks for both online and manual orders
                    const fullTotal = paymentInfo.fullTotal || order.total || order.totalAmount || order.pricing?.total || 0;
                    const paidAmount = paymentInfo.paidAmount || order.amountPaid || 0;
                    
                    // Calculate remaining amount properly
                    let remainingAmount = paymentInfo.remainingAmount || order.amountDue;
                    if (remainingAmount === undefined || remainingAmount === null) {
                      // If remainingAmount is not set, calculate it
                      remainingAmount = Math.max(0, fullTotal - paidAmount);
                    }
                    
                      // Check for explicit payment status field (from manual order editing)
                      const explicitPaymentStatus = order.paymentStatus;
                    const hasRemainingBalance = remainingAmount > 0;
                    
                    // Determine if this looks like an incomplete payment record
                    const hasPaymentInfo = paymentInfo.fullTotal !== undefined || paymentInfo.paidAmount !== undefined || paymentInfo.remainingAmount !== undefined ||
                                          order.amountPaid !== undefined || order.totalAmount !== undefined || order.amountDue !== undefined;
                    const isLegacyOrder = !hasPaymentInfo && fullTotal > 0;
                    
                    // Determine payment type and styling for print
                    let paymentType, cssClass, emoji, displayText;
                    
                    if (isLegacyOrder) {
                      paymentType = 'Legacy';
                      cssClass = 'payment-collect';
                      emoji = '⚠️';
                      displayText = 'VERIFY';
                      } else if (explicitPaymentStatus === 'paid' || (!hasRemainingBalance && (remainingAmount === 0 || fullTotal === paidAmount))) {
                        // Honor explicit payment status OR calculated payment completion
                      paymentType = 'Paid';
                      cssClass = 'payment-paid';
                      emoji = '✅';
                      displayText = 'PAID';
                      } else if (explicitPaymentStatus === 'deposit') {
                        paymentType = 'Deposit';
                        cssClass = 'payment-deposit';
                        emoji = '💳';
                        displayText = 'COLLECT';
                      } else if (explicitPaymentStatus === 'partial') {
                        paymentType = 'Partially Paid';
                        cssClass = 'payment-collect';
                        emoji = '💸';
                        displayText = 'COLLECT';
                    } else {
                        // Fallback to original logic when no explicit status is set
                      // Determine if it's a deposit or partial payment
                      if (paymentOption === 'deposit') {
                        paymentType = 'Deposit';
                        cssClass = 'payment-deposit';
                        emoji = '💳';
                        displayText = 'COLLECT';
                      } else {
                        // Check if they paid exactly the deposit amount
                        const depositAmount = isTestOrder ? 1 : 50;
                        if (paidAmount === depositAmount) {
                          paymentType = 'Deposit';
                          cssClass = 'payment-deposit';
                          emoji = '💳';
                          displayText = 'COLLECT';
                        } else {
                          paymentType = 'Partially Paid';
                          cssClass = 'payment-collect';
                          emoji = '💸';
                          displayText = 'COLLECT';
                        }
                      }
                    }
                    
                    const amount = isLegacyOrder ? 'Check Status' : 
                                    explicitPaymentStatus === 'paid' ? '$0.00' :
                                  hasRemainingBalance ? `$${remainingAmount.toFixed(2)}` : '$0.00';
                    const note = isTestOrder ? 'Test Order' : 
                                isLegacyOrder ? 'Check payment status' :
                                  (explicitPaymentStatus === 'paid' || !hasRemainingBalance) ? 'No collection needed' : paymentType;
                    
                    return `<div class="${cssClass}">${emoji} ${displayText}<br/>${amount}<br/><small>${note}</small></div>`;
                  })()}
                </td>
                <td>
                  <span class="order-status status-${(order.status || 'pending').toLowerCase().replace(' ', '-')}">
                    ${order.status || 'Pending'}
                  </span>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="footer">
          <p>Total Orders: ${ordersToPrint.length}</p>
          <p>Generated on ${formattedDate}</p>
          <button class="no-print" onclick="window.print()">Print This Report</button>
        </div>
      </body>
      </html>
    `;
    
    // Write the content to the new window and print
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Automatically print after the content is loaded
    printWindow.onload = function() {
      // Give a moment for styles to apply
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 500);
    };
  };
  
  // Get current page orders
  const getCurrentPageOrders = () => {
    const indexOfLastOrder = currentPage * ordersPerPage;
    const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
    return filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  };
  
  // Handle order status change
  const handleStatusChange = async (orderId, newStatus) => {
    try {
      setIsUpdatingStatus(orderId);
      await updateOrderStatus(orderId, newStatus);
      
      // Update the order in the local state
      const updatedOrders = orders.map(order => {
        if (order.id === orderId) {
          const updatedOrder = { ...order, status: newStatus };
          
          // If status is "completed", automatically mark as paid and archive
          if (newStatus && newStatus.toLowerCase() === 'completed') {
            const paymentInfo = order.paymentInfo || {};
            const fullTotal = paymentInfo.fullTotal || order.total || order.totalAmount || order.pricing?.total || 0;
            
            updatedOrder.paymentStatus = 'paid';
            updatedOrder.paymentInfo = {
              ...paymentInfo,
              paidAmount: fullTotal,
              remainingAmount: 0
            };
            updatedOrder.amountPaid = fullTotal;
            updatedOrder.amountDue = 0;
            updatedOrder.archived = true;
            updatedOrder.archivedAt = new Date();
          }
          
          return updatedOrder;
        }
        return order;
      });
      
      setOrders(updatedOrders);
      setFilteredOrders(prevFiltered => 
        prevFiltered.map(order => {
          if (order.id === orderId) {
            const updatedOrder = { ...order, status: newStatus };
            
            // If status is "completed", automatically mark as paid and archive
            if (newStatus && newStatus.toLowerCase() === 'completed') {
              const paymentInfo = order.paymentInfo || {};
              const fullTotal = paymentInfo.fullTotal || order.total || order.totalAmount || order.pricing?.total || 0;
              
              updatedOrder.paymentStatus = 'paid';
              updatedOrder.paymentInfo = {
                ...paymentInfo,
                paidAmount: fullTotal,
                remainingAmount: 0
              };
              updatedOrder.amountPaid = fullTotal;
              updatedOrder.amountDue = 0;
              updatedOrder.archived = true;
              updatedOrder.archivedAt = new Date();
            }
            
            return updatedOrder;
          }
          return order;
        })
      );
      
      // Show success message
      const message = newStatus && newStatus.toLowerCase() === 'completed' 
        ? `Order marked as ${newStatus}, paid in full, and archived`
        : `Order status updated to ${newStatus}`;
      alert(message);
    } catch (err) {
      console.error('Error updating order status:', err);
      alert('Failed to update order status. Please try again.');
    } finally {
      setIsUpdatingStatus(null);
    }
  };
  
  // Handle order archive/unarchive
  const handleArchiveOrder = async (orderId, archived) => {
    try {
      setIsUpdatingStatus(orderId);
      await updateOrderArchiveStatus(orderId, archived);
      
      // Update the order in the local state
      const updatedOrders = orders.map(order => 
        order.id === orderId ? { ...order, archived, archivedAt: archived ? new Date() : null } : order
      );
      
      setOrders(updatedOrders);
      setFilteredOrders(prevFiltered => 
        prevFiltered.map(order => 
          order.id === orderId ? { ...order, archived, archivedAt: archived ? new Date() : null } : order
        )
      );
      
      // Show success message
      const message = archived 
        ? 'Order archived successfully'
        : 'Order unarchived successfully';
      alert(message);
    } catch (err) {
      console.error('Error updating order archive status:', err);
      alert('Failed to update order archive status. Please try again.');
    } finally {
      setIsUpdatingStatus(null);
    }
  };
  
  // Toggle order selection
  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  // Toggle select mode
  const toggleSelectMode = () => {
    setSelectMode(prev => !prev);
    // If turning off select mode, clear selections
    if (selectMode) {
      setSelectedOrders({});
    }
  };

  // Select/deselect all orders on current page
  const toggleSelectAll = () => {
    const currentPageOrders = getCurrentPageOrders();
    const allSelected = currentPageOrders.every(order => selectedOrders[order.id]);
    
    const newSelectedOrders = { ...selectedOrders };
    
    currentPageOrders.forEach(order => {
      newSelectedOrders[order.id] = !allSelected;
    });
    
    setSelectedOrders(newSelectedOrders);
  };

  // Get count of selected orders
  const getSelectedOrdersCount = () => {
    return Object.values(selectedOrders).filter(selected => selected).length;
  };
  
  // Get array of selected orders
  const getSelectedOrdersArray = () => {
    return filteredOrders.filter(order => selectedOrders[order.id]);
  };

  // Format date for delivery - Fixed to prevent timezone shifts
  const formatDeliveryDate = (order) => {
    // Check multiple possible locations for delivery date
    const deliveryDate = order.rentalDate || 
                         order.deliveryDate || 
                         order.delivery?.date || 
                         order.deliveryInfo?.deliveryDate ||
                         order.deliveryInfo?.startDate;
    if (!deliveryDate) return 'TBD';
    
    // Handle different date formats properly to avoid timezone issues
    let dateObj;
    if (typeof deliveryDate === 'string') {
      // For date strings like "2025-06-21", create date in local timezone to prevent UTC shifts
      if (deliveryDate.includes('-') && deliveryDate.length === 10) {
        const [year, month, day] = deliveryDate.split('-');
        // Create date at noon local time to avoid timezone boundary issues
        dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0);
      } else {
        // For other string formats, parse carefully
        const parsedDate = new Date(deliveryDate);
        // If the parsed date seems to be offset, adjust it
        if (deliveryDate.includes('T') || deliveryDate.includes('Z')) {
          // This is likely an ISO string, convert back to local
          dateObj = new Date(parsedDate.getTime() + (parsedDate.getTimezoneOffset() * 60000));
        } else {
          dateObj = parsedDate;
        }
      }
    } else {
      // For Firebase Timestamp objects or Date objects
      dateObj = deliveryDate.toDate ? deliveryDate.toDate() : new Date(deliveryDate);
    }
    
    // Ensure we have a valid date
    if (isNaN(dateObj.getTime())) {
      console.warn('Invalid delivery date encountered:', deliveryDate);
      return 'Invalid Date';
    }
    
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      weekday: 'short'
    };
    
    const formatted = dateObj.toLocaleDateString(undefined, options);
    
    // Add time if available - check multiple possible locations
    const startTime = order.startTime || 
                     order.deliveryInfo?.time || 
                     order.delivery?.time ||
                     order.rentalTime;
    if (startTime) {
      return `${formatted} ${startTime}`;
    }
    
    return formatted;
  };

  // Get delivery status for styling
  const getDeliveryStatus = (order) => {
    // Check multiple possible locations for delivery date
    const deliveryDate = order.rentalDate || 
                         order.deliveryDate || 
                         order.delivery?.date || 
                         order.deliveryInfo?.deliveryDate ||
                         order.deliveryInfo?.startDate;
    if (!deliveryDate) return 'no-date';
    
    // If order status is "completed", always show as "delivered" regardless of payment status
    // This indicates the order has been physically delivered and completed
    if (order.status && order.status.toLowerCase() === 'completed') {
      return 'delivered';
    }
    
    // Handle different date formats properly to avoid timezone issues
    let dateObj;
    if (typeof deliveryDate === 'string') {
      // For date strings like "2025-06-21", create date in local timezone to prevent UTC shifts
      if (deliveryDate.includes('-') && deliveryDate.length === 10) {
        const [year, month, day] = deliveryDate.split('-');
        // Create date at noon local time to avoid timezone boundary issues
        dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0);
      } else {
        // For other string formats, parse carefully
        const parsedDate = new Date(deliveryDate);
        // If the parsed date seems to be offset, adjust it
        if (deliveryDate.includes('T') || deliveryDate.includes('Z')) {
          // This is likely an ISO string, convert back to local
          dateObj = new Date(parsedDate.getTime() + (parsedDate.getTimezoneOffset() * 60000));
        } else {
          dateObj = parsedDate;
        }
      }
    } else {
      // For Firebase Timestamp objects or Date objects
      dateObj = deliveryDate.toDate ? deliveryDate.toDate() : new Date(deliveryDate);
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to midnight for accurate comparison
    
    dateObj.setHours(0, 0, 0, 0); // Set to midnight for accurate comparison
    
    const daysDiff = Math.ceil((dateObj - today) / (1000 * 60 * 60 * 24));
    
    // Check payment status - if fully paid, don't show as overdue
    const paymentInfo = order.paymentInfo || {};
    const fullTotal = paymentInfo.fullTotal || order.total || order.totalAmount || order.pricing?.total || 0;
    const paidAmount = paymentInfo.paidAmount || order.amountPaid || 0;
    
    // Calculate remaining amount properly
    let remainingAmount = paymentInfo.remainingAmount || order.amountDue;
    if (remainingAmount === undefined || remainingAmount === null) {
      remainingAmount = Math.max(0, fullTotal - paidAmount);
    }
    
    const isFullyPaid = remainingAmount <= 0;
    
    // If past due but fully paid, show as 'delivered' instead of 'past-due'
    if (daysDiff < 0) {
      return isFullyPaid ? 'delivered' : 'past-due';
    }
    if (daysDiff === 0) return 'today';
    if (daysDiff === 1) return 'tomorrow';
    if (daysDiff <= 7) return 'this-week';
    return 'future';
  };

  // Determine inventory source based on products in order
  const getInventorySource = (order) => {
    if (!order.items || order.items.length === 0) return 'Unknown';
    
    // Angola office inventory (outdoor/water items)
    const angolaInventory = [
      'Beach Oasis', 'Mini Mouse', 'Toy Story', 'SunShine WaterSlide', 
      'Dino', 'America', 'Generac 6500', 'Beach Oasis Combo', 
      'Minnie Mouse Combo', 'Toy Story Combo', 'Sunshine XL Waterslide',
      'Dino Kingdom Combo', 'America Patriot Combo'
    ];
    
    // Elkhart office inventory (combo units)
    const elkhartInventory = [
      'Blue Marble Combo', 'Carnival Combo', 'XL Tropical Dual Lane Combo',
      'RAINBOW DUAL LANE COMBO', 'Rainbow Dual Lane Combo', 'Jellybean Dual Lane Combo'
    ];
    
    let angolaItems = 0;
    let elkhartItems = 0;
    
    order.items.forEach(item => {
      const itemName = item.name || '';
      
      // Check if item belongs to Angola inventory
      if (angolaInventory.some(inv => 
        itemName.toLowerCase().includes(inv.toLowerCase()) ||
        inv.toLowerCase().includes(itemName.toLowerCase())
      )) {
        angolaItems++;
      }
      
      // Check if item belongs to Elkhart inventory
      if (elkhartInventory.some(inv => 
        itemName.toLowerCase().includes(inv.toLowerCase()) ||
        inv.toLowerCase().includes(itemName.toLowerCase())
      )) {
        elkhartItems++;
      }
    });
    
    // Determine primary source
    if (angolaItems > elkhartItems) {
      return elkhartItems > 0 ? 'Angola + Elkhart' : 'Angola';
    } else if (elkhartItems > angolaItems) {
      return angolaItems > 0 ? 'Elkhart + Angola' : 'Elkhart';
    } else if (angolaItems > 0 && elkhartItems > 0) {
      return 'Mixed Inventory';
    }
    
    return 'Unknown';
  };

  const handleCreateManualOrder = () => {
    setEditingOrder(null);
    setShowManualOrderForm(true);
  };

  const handleEditOrder = (order) => {
    setEditingOrder(order);
    setShowManualOrderForm(true);
  };

  const handleCloseManualOrderForm = () => {
    setShowManualOrderForm(false);
    setEditingOrder(null);
  };

  const handleSaveManualOrder = () => {
    setShowManualOrderForm(false);
    setEditingOrder(null);
    // Refresh orders list
    fetchOrders();
  };

  const handleDeleteOrder = async (orderId, orderNumber) => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to delete order #${orderNumber || orderId}?\n\nThis action cannot be undone.`
    );
    
    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);
      
      // Delete from Firebase
      await deleteOrder(orderId);
      
      // Update local state by removing the deleted order
      setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
      setFilteredOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
      
      // Clear selection if the deleted order was selected
      setSelectedOrders(prevSelected => {
        const newSelected = { ...prevSelected };
        delete newSelected[orderId];
        return newSelected;
      });
      
      console.log(`Order ${orderNumber || orderId} deleted successfully`);
      
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('Failed to delete order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSelectedOrders = async () => {
    const selectedOrdersArray = getSelectedOrdersArray();
    const selectedCount = selectedOrdersArray.length;
    
    if (selectedCount === 0) {
      alert('No orders selected for deletion.');
      return;
    }
    
    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedCount} selected order${selectedCount > 1 ? 's' : ''}?\n\nThis action cannot be undone.`
    );
    
    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);
      
      // Delete all selected orders from Firebase
      const deletePromises = selectedOrdersArray.map(order => deleteOrder(order.id));
      await Promise.all(deletePromises);
      
      // Get the IDs of deleted orders
      const deletedOrderIds = selectedOrdersArray.map(order => order.id);
      
      // Update local state by removing the deleted orders
      setOrders(prevOrders => prevOrders.filter(order => !deletedOrderIds.includes(order.id)));
      setFilteredOrders(prevOrders => prevOrders.filter(order => !deletedOrderIds.includes(order.id)));
      
      // Clear all selections
      setSelectedOrders({});
      setSelectMode(false);
      
      console.log(`${selectedCount} orders deleted successfully`);
      alert(`${selectedCount} order${selectedCount > 1 ? 's' : ''} deleted successfully.`);
      
    } catch (error) {
      console.error('Error deleting selected orders:', error);
      alert('Failed to delete some orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle orders per page change
  const handleOrdersPerPageChange = (e) => {
    const newOrdersPerPage = parseInt(e.target.value);
    setOrdersPerPage(newOrdersPerPage);
    setSelectedOrders({}); // Clear selections when changing page size
    setSelectMode(false); // Exit select mode
  };

  // Generate page numbers for pagination
  const generatePageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5; // Show up to 5 page numbers at a time
    
    if (totalPages <= maxVisiblePages) {
      // If total pages is small, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show a range of pages around the current page
      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      
      // Adjust start if we're near the end
      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      
      // Add first page and ellipsis if needed
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push('...');
        }
      }
      
      // Add visible page range
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add ellipsis and last page if needed
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('...');
        }
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  if (loading && orders.length === 0) {
    return (
      <div className="dashboard-section orders-section">
        <div className="loading-indicator">Loading orders...</div>
      </div>
    );
  }
  
  if (error && orders.length === 0) {
    return (
      <div className="dashboard-section orders-section">
        <div className="error-message">{error}</div>
      </div>
    );
  }
  
  return (
    <div className="dashboard-section orders-section">
      {/* DEBUG: Uncomment the line below to enable Firebase image debugging */}
      {/* <ImageDebugComponent /> */}
      <style>
        {`
          .filter-controls {
            display: flex;
            flex-wrap: wrap;
            gap: 15px;
            margin-bottom: 20px;
            align-items: center;
          }
          
          .search-box, .filter-box, .sort-box, .date-filter-box, .per-page-box {
            display: flex;
            align-items: center;
            background-color: #f5f5f5;
            border-radius: 4px;
            padding: 5px 10px;
          }
          
          .date-filter-box {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          
          .date-filter-box input[type="date"] {
            border: 1px solid #ddd;
            border-radius: 4px;
            padding: 5px 8px;
            font-size: 14px;
          }
          
          .date-filter-box .fa-calendar-alt {
            color: #666;
            margin-right: 5px;
          }
          
          .icon-button {
            background-color: #f5f5f5;
            color: #333;
            border: none;
            border-radius: 4px;
            padding: 8px 12px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
          }
          
          .icon-button:hover {
            background-color: #e0e0e0;
          }
          
          .icon-button.active {
            background-color: #4a90e2;
            color: white;
          }
          
          .icon-button.print {
            background-color: #28a745;
            color: white;
          }
          
          .icon-button.print:hover {
            background-color: #218838;
          }
          
          .selection-count {
            position: absolute;
            top: -8px;
            right: -8px;
            background-color: #dc3545;
            color: white;
            border-radius: 50%;
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: bold;
          }
          
          .checkbox-column {
            width: 40px;
            text-align: center;
          }
          
          .selected-row {
            background-color: #e8f4fd !important;
          }
          
          @media print {
            .filter-controls, .pagination-controls {
              display: none;
            }
          }
          
          .delivery-status {
            padding: 4px 8px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: bold;
            text-align: center;
            display: inline-block;
            min-width: 60px;
          }
          
          .delivery-status.today {
            background-color: #ff6b6b;
            color: white;
          }
          
          .delivery-status.tomorrow {
            background-color: #ffa726;
            color: white;
          }
          
          .delivery-status.this-week {
            background-color: #66bb6a;
            color: white;
          }
          
          .delivery-status.future {
            background-color: #42a5f5;
            color: white;
          }
          
          .delivery-status.past-due {
            background-color: #ef5350;
            color: white;
          }
          
          .delivery-status.delivered {
            background-color: #4caf50;
            color: white;
          }
          
          .delivery-status.no-date {
            background-color: #bdbdbd;
            color: white;
          }
          
          .delivery-date-cell {
            white-space: nowrap;
            font-size: 13px;
          }
          
          .print-delivery-view {
            background-color: #e3f2fd;
            padding: 10px;
            border-radius: 8px;
            margin-bottom: 15px;
            border-left: 4px solid #2196f3;
          }
          
          .print-delivery-view h3 {
            margin: 0 0 10px 0;
            color: #1976d2;
            display: flex;
            align-items: center;
            gap: 10px;
          }
          
          @media print {
            .delivery-status {
              background-color: #f0f0f0 !important;
              color: #333 !important;
              border: 1px solid #ccc !important;
            }
            
            .print-delivery-view {
              border: 2px solid #333 !important;
              background-color: #f9f9f9 !important;
            }
          }

          .pagination-controls {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 20px;
            padding: 15px 0;
            border-top: 1px solid #f0f0f0;
          }

          .pagination-info {
            font-size: 14px;
            color: #666;
          }

          .pagination-buttons {
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .pagination-button {
            background-color: white;
            border: 1px solid #e0e0e0;
            padding: 8px 12px;
            border-radius: 4px;
            font-size: 14px;
            cursor: pointer;
            transition: all 0.2s;
          }

          .pagination-button:hover:not(:disabled) {
            background-color: #f0f0f0;
            border-color: #1E88E5;
          }

          .pagination-button:disabled {
            color: #bdbdbd;
            cursor: not-allowed;
            background-color: #f9f9f9;
          }

          .pagination-button.active {
            background-color: #1E88E5;
            color: white;
            border-color: #1E88E5;
          }

          .pagination-button.active:hover {
            background-color: #1976D2;
            border-color: #1976D2;
          }

          .page-numbers {
            display: flex;
            align-items: center;
            gap: 5px;
          }

          .page-indicator {
            padding: 8px 12px;
            font-size: 14px;
            font-weight: 500;
            color: #333;
          }

          @media (max-width: 768px) {
            .pagination-controls {
              flex-direction: column;
              gap: 10px;
              text-align: center;
            }

            .pagination-buttons {
              flex-wrap: wrap;
              justify-content: center;
            }

            .page-numbers {
              order: 1;
              margin: 5px 0;
            }

            .pagination-button {
              padding: 6px 10px;
              font-size: 12px;
            }
          }
        `}
      </style>
      
      {showOrderDetails && selectedOrder ? (
        <OrderDetails 
          order={selectedOrder} 
          onClose={handleCloseOrderDetails}
          onStatusUpdate={(newStatus) => handleUpdateStatus(selectedOrder.id, newStatus)}
        />
      ) : (
        <>
          <div className="section-header">
            <h2>Orders Management</h2>
            <button 
              className="create-manual-order-button"
              onClick={handleCreateManualOrder}
            >
              <FontAwesomeIcon icon={faPlus} /> Create Manual Order
            </button>
          </div>
          
          <div className="filter-controls">
            <div className="search-box">
              <FontAwesomeIcon icon={faSearch} />
              <input
                type="text"
                placeholder="Search orders..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
            
            <div className="filter-box">
              <FontAwesomeIcon icon={faFilter} />
              <select 
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="confirmed">Confirmed</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            
            <div className="filter-box">
              <FontAwesomeIcon icon={faFilter} />
              <select 
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
              >
                <option value="all">All Locations</option>
                <option value="Angola">🏢 Angola Office</option>
                <option value="Elkhart">🏢 Elkhart Office</option>
                <option value="Delivery Area">--- Delivery Areas ---</option>
                <option value="Angola Area">Angola Service Area</option>
                <option value="Elkhart Area">Elkhart Service Area</option>
                <option value="Mishawaka">Mishawaka</option>
                <option value="Goshen">Goshen</option>
                <option value="Niles">Niles</option>
                <option value="Dowagiac">Dowagiac</option>
                <option value="Bremen">Bremen</option>
              </select>
            </div>
            
            <div className="filter-box">
              <FontAwesomeIcon icon={faArchive} />
              <select 
                value={archivedFilter}
                onChange={(e) => setArchivedFilter(e.target.value)}
                title="Filter by archive status"
              >
                <option value="active">📋 Active Orders</option>
                <option value="archived">📦 Archived Orders</option>
                <option value="all">📄 All Orders</option>
              </select>
            </div>
            
            <div className="sort-box">
              <FontAwesomeIcon icon={faSortAmountDown} />
              <select 
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="delivery-asc">🚚 Delivery Date (Earliest First)</option>
                <option value="delivery-desc">🚚 Delivery Date (Latest First)</option>
                <option value="inventory-angola">🏢 Angola Inventory First</option>
                <option value="inventory-elkhart">🏢 Elkhart Inventory First</option>
                <option value="location-asc">📍 Delivery Location (A-Z)</option>
                <option value="location-desc">📍 Delivery Location (Z-A)</option>
                <option value="date-desc">📅 Order Date (Newest First)</option>
                <option value="date-asc">📅 Order Date (Oldest First)</option>
                <option value="total-desc">💰 Total (High to Low)</option>
                <option value="total-asc">💰 Total (Low to High)</option>
              </select>
            </div>
            
            <div className="date-filter-box">
              <FontAwesomeIcon icon={faCalendarAlt} />
              <span style={{fontSize: '12px', marginRight: '8px'}}>Delivery Date:</span>
              <input
                type="date"
                placeholder="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                title="Filter by delivery date range"
              />
              <input
                type="date"
                placeholder="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                title="Filter by delivery date range"
              />
            </div>

            <div className="per-page-box">
              <FontAwesomeIcon icon={faFilter} />
              <select 
                value={ordersPerPage}
                onChange={handleOrdersPerPageChange}
                title="Orders per page"
              >
                <option value={10}>Show 10</option>
                <option value={25}>Show 25</option>
                <option value={50}>Show 50</option>
                <option value={100}>Show 100</option>
              </select>
            </div>
            
            <button 
              className={`icon-button ${selectMode ? 'active' : ''}`} 
              title={selectMode ? "Exit Selection Mode" : "Enter Selection Mode"}
              onClick={toggleSelectMode}
            >
              <FontAwesomeIcon icon={selectMode ? faTimesCircle : faFilter} />
              {selectMode && getSelectedOrdersCount() > 0 && 
                <span className="selection-count">{getSelectedOrdersCount()}</span>
              }
            </button>
            
            <button 
              className="icon-button print" 
              title={getSelectedOrdersCount() > 0 ? `Print ${getSelectedOrdersCount()} Selected Orders` : "Print All Filtered Orders"}
              onClick={handlePrintOrders}
            >
              <FontAwesomeIcon icon={faPrint} />
            </button>

            {selectMode && getSelectedOrdersCount() > 0 && (
              <button 
                className="icon-button delete" 
                title={`Delete ${getSelectedOrdersCount()} Selected Order${getSelectedOrdersCount() > 1 ? 's' : ''}`}
                onClick={handleDeleteSelectedOrders}
                disabled={loading}
              >
                <FontAwesomeIcon icon={faTrash} />
                <span style={{marginLeft: '4px', fontSize: '12px'}}>
                  Delete ({getSelectedOrdersCount()})
                </span>
              </button>
            )}
            
            <button 
              className={`icon-button ${(sortBy === 'delivery-asc' || sortBy === 'delivery-desc' || sortBy.includes('inventory')) ? 'active' : ''}`}
              title="View Delivery & Inventory Schedule"
              onClick={() => {
                if (sortBy === 'delivery-asc') setSortBy('inventory-angola');
                else if (sortBy === 'inventory-angola') setSortBy('inventory-elkhart');
                else if (sortBy === 'inventory-elkhart') setSortBy('delivery-desc');
                else setSortBy('delivery-asc');
              }}
              style={{
                backgroundColor: (sortBy === 'delivery-asc' || sortBy === 'delivery-desc' || sortBy.includes('inventory')) ? '#4a90e2' : '#28a745',
                color: 'white'
              }}
            >
              <FontAwesomeIcon icon={faTruck} />
              <span style={{marginLeft: '8px', fontSize: '12px'}}>
                {sortBy.includes('inventory-angola') ? 'Angola' :
                 sortBy.includes('inventory-elkhart') ? 'Elkhart' :
                 'Schedule'}
              </span>
            </button>
          </div>
          
          <div className="data-table-container">
            <style>
              {`
                .delivery-status {
                  padding: 4px 8px;
                  border-radius: 12px;
                  font-size: 12px;
                  font-weight: bold;
                  text-align: center;
                  display: inline-block;
                  min-width: 60px;
                }
                
                .delivery-status.today {
                  background-color: #ff6b6b;
                  color: white;
                }
                
                .delivery-status.tomorrow {
                  background-color: #ffa726;
                  color: white;
                }
                
                .delivery-status.this-week {
                  background-color: #66bb6a;
                  color: white;
                }
                
                .delivery-status.future {
                  background-color: #42a5f5;
                  color: white;
                }
                
                .delivery-status.past-due {
                  background-color: #ef5350;
                  color: white;
                }
                
                .delivery-status.delivered {
                  background-color: #4caf50;
                  color: white;
                }
                
                .delivery-status.no-date {
                  background-color: #bdbdbd;
                  color: white;
                }
                
                .delivery-date-cell {
                  white-space: nowrap;
                  font-size: 13px;
                }
                
                .print-delivery-view {
                  background-color: #e3f2fd;
                  padding: 10px;
                  border-radius: 8px;
                  margin-bottom: 15px;
                  border-left: 4px solid #2196f3;
                }
                
                .print-delivery-view h3 {
                  margin: 0 0 10px 0;
                  color: #1976d2;
                  display: flex;
                  align-items: center;
                  gap: 10px;
                }
                
                @media print {
                  .delivery-status {
                    background-color: #f0f0f0 !important;
                    color: #333 !important;
                    border: 1px solid #ccc !important;
                  }
                  
                  .delivery-status.delivered {
                    background-color: #e8f5e9 !important;
                    color: #2e7d32 !important;
                    border: 1px solid #4caf50 !important;
                  }
                  
                  .print-delivery-view {
                    border: 2px solid #333 !important;
                    background-color: #f9f9f9 !important;
                  }
                }
              `}
            </style>
            
            {(sortBy === 'delivery-asc' || sortBy === 'delivery-desc' || sortBy.includes('inventory')) && (
              <div className="print-delivery-view">
                <h3>
                  <FontAwesomeIcon icon={faTruck} />
                  {sortBy.includes('inventory') ? 'Inventory & Delivery Schedule' : 'Delivery Schedule'} - {formatDate(new Date())}
                  {locationFilter !== 'all' && ` - ${locationFilter} Location`}
                </h3>
                <p>
                  {sortBy.includes('inventory') ? 
                    'Orders organized by inventory source for efficient logistics planning' :
                    'Orders sorted by delivery date for easy planning and routing'
                  }
                  {locationFilter !== 'all' && ` (${locationFilter} location only)`}
                </p>
                {sortBy.includes('inventory') && (
                  <div style={{
                    display: 'flex', 
                    gap: '15px', 
                    marginTop: '10px', 
                    fontSize: '12px',
                    color: '#666'
                  }}>
                    <span>🟢 Angola Office: Outdoor/Water items</span>
                    <span>🔵 Elkhart Office: Combo units</span>
                    <span>🟠 Mixed: Cross-location inventory</span>
                  </div>
                )}
              </div>
            )}
            
            <table className="data-table">
              <thead>
                <tr>
                  {selectMode && (
                    <th className="checkbox-column">
                      <input 
                        type="checkbox" 
                        checked={getCurrentPageOrders().length > 0 && getCurrentPageOrders().every(order => selectedOrders[order.id])}
                        onChange={toggleSelectAll}
                      />
                    </th>
                  )}
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Delivery Date</th>
                  <th>Location</th>
                  <th>Order Date</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {getCurrentPageOrders().length > 0 ? (
                  getCurrentPageOrders().map(order => (
                    <tr key={order.id} className={selectedOrders[order.id] ? 'selected-row' : ''}>
                      {selectMode && (
                        <td className="checkbox-column">
                          <input 
                            type="checkbox" 
                            checked={!!selectedOrders[order.id]} 
                            onChange={() => toggleOrderSelection(order.id)}
                          />
                        </td>
                      )}
                      <td>
                        <strong>#{order.orderNumber || order.id.substring(0, 8)}</strong>
                      </td>
                      <td className="customer-cell">
                        {order.customerName ||
                         (order.customer?.name) ||
                         (order.customerInfo?.name) ||
                         (order.customerInfo?.firstName && order.customerInfo?.lastName ?
                           `${order.customerInfo.firstName} ${order.customerInfo.lastName}` :
                           (order.customer?.firstName && order.customer?.lastName ?
                             `${order.customer.firstName} ${order.customer.lastName}` :
                             'Guest'))}
                      </td>
                      <td className="delivery-date-cell">
                        <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                          <span>{formatDeliveryDate(order)}</span>
                          <span className={`delivery-status ${getDeliveryStatus(order)}`}>
                            {(() => {
                              const status = getDeliveryStatus(order);
                              switch(status) {
                                case 'today': return 'TODAY';
                                case 'tomorrow': return 'TOMORROW';
                                case 'this-week': return 'THIS WEEK';
                                case 'past-due': return 'OVERDUE';
                                case 'delivered': return 'DELIVERED';
                                case 'no-date': return 'NO DATE';
                                default: return 'UPCOMING';
                              }
                            })()}
                          </span>
                        </div>
                      </td>
                      <td>
                        {(() => {
                          const inventorySource = getInventorySource(order);
                          return (
                            <span className="inventory-source" style={{
                              display: 'inline-block',
                              background: inventorySource === 'Angola' ? '#e8f5e9' :
                                         inventorySource === 'Elkhart' ? '#e3f2fd' :
                                         inventorySource.includes('+') ? '#fff3e0' : '#f5f5f5',
                              color: inventorySource === 'Angola' ? '#2e7d32' :
                                     inventorySource === 'Elkhart' ? '#1976d2' :
                                     inventorySource.includes('+') ? '#f57c00' : '#666',
                              padding: '3px 10px',
                              borderRadius: '8px',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              whiteSpace: 'nowrap'
                            }}>
                              {inventorySource}
                            </span>
                          );
                        })()}
                      </td>
                      <td>{formatDate(order.createdAt)}</td>
                      <td>{order.items?.length || 0}</td>
                      <td>{formatCurrency(order.total || order.totalAmount || 0)}</td>
                      <td>
                        {(() => {
                          // Enhanced payment status logic with better validation
                          const paymentInfo = order.paymentInfo || {};
                          const paymentOption = order.paymentOption || paymentInfo.paymentOption;
                          const isTestOrder = paymentInfo.isTestOrder || order.isTestOrder || false;
                          
                          // Get amounts with proper fallbacks for both online and manual orders
                          const fullTotal = paymentInfo.fullTotal || order.total || order.totalAmount || order.pricing?.total || 0;
                          const paidAmount = paymentInfo.paidAmount || order.amountPaid || 0;
                          
                          // Calculate remaining amount properly
                          let remainingAmount = paymentInfo.remainingAmount || order.amountDue;
                          if (remainingAmount === undefined || remainingAmount === null) {
                            // If remainingAmount is not set, calculate it
                            remainingAmount = Math.max(0, fullTotal - paidAmount);
                          }
                          
                          // Check for explicit payment status field (from manual order editing)
                          const explicitPaymentStatus = order.paymentStatus;
                          const hasRemainingBalance = remainingAmount > 0;
                          
                          // Determine if this looks like an incomplete payment record
                          const hasPaymentInfo = paymentInfo.fullTotal !== undefined || paymentInfo.paidAmount !== undefined || paymentInfo.remainingAmount !== undefined ||
                                                order.amountPaid !== undefined || order.totalAmount !== undefined || order.amountDue !== undefined;
                          const isLegacyOrder = !hasPaymentInfo && fullTotal > 0;

                          // Determine payment type and styling
                          let paymentType, backgroundColor, borderColor, textColor, emoji;
                          
                          if (isLegacyOrder) {
                            paymentType = 'Legacy';
                            backgroundColor = '#fff3e0';
                            borderColor = '#ff9800';
                            textColor = '#ef6c00';
                            emoji = '⚠️';
                          } else if (explicitPaymentStatus === 'paid' || (!hasRemainingBalance && (remainingAmount === 0 || fullTotal === paidAmount))) {
                            // Honor explicit payment status OR calculated payment completion
                            paymentType = 'Paid';
                            backgroundColor = '#e8f5e9';
                            borderColor = '#4caf50';
                            textColor = '#2e7d32';
                            emoji = '✅';
                          } else if (explicitPaymentStatus === 'deposit') {
                            paymentType = 'Deposit';
                            backgroundColor = '#e3f2fd';
                            borderColor = '#2196f3';
                            textColor = '#1976d2';
                            emoji = '💳';
                          } else if (explicitPaymentStatus === 'partial') {
                            paymentType = 'Partially Paid';
                            backgroundColor = '#fff3e0';
                            borderColor = '#ff9800';
                            textColor = '#ef6c00';
                            emoji = '💸';
                          } else {
                            // Fallback to original logic when no explicit status is set
                            // Determine if it's a deposit or partial payment
                            if (paymentOption === 'deposit') {
                              paymentType = 'Deposit';
                              backgroundColor = '#e3f2fd';
                              borderColor = '#2196f3';
                              textColor = '#1976d2';
                              emoji = '💳';
                            } else {
                              // Check if they paid exactly the deposit amount ($50 or $1 for test)
                              const depositAmount = isTestOrder ? 1 : 50;
                              if (paidAmount === depositAmount) {
                                paymentType = 'Deposit';
                                backgroundColor = '#e3f2fd';
                                borderColor = '#2196f3';
                                textColor = '#1976d2';
                                emoji = '💳';
                              } else {
                                paymentType = 'Partially Paid';
                                backgroundColor = '#fff3e0';
                                borderColor = '#ff9800';
                                textColor = '#ef6c00';
                                emoji = '💸';
                              }
                            }
                          }
                          
                          return (
                            <div style={{
                              display: 'inline-flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '1px',
                              padding: '3px 6px',
                              borderRadius: '6px',
                              background: backgroundColor,
                              border: `1px solid ${borderColor}`
                            }}>
                              <div style={{
                                fontSize: '12px',
                                fontWeight: 'bold',
                                color: textColor,
                                textAlign: 'center',
                                whiteSpace: 'nowrap'
                              }}>
                                {isLegacyOrder ? 'VERIFY' :
                                 explicitPaymentStatus === 'paid' ? 'PAID' :
                                 hasRemainingBalance ? `$${remainingAmount.toFixed(2)}` : 'PAID'}
                              </div>
                              <div style={{
                                fontSize: '9px',
                                color: textColor,
                                textAlign: 'center',
                                lineHeight: '1.1',
                                opacity: 0.9,
                                whiteSpace: 'nowrap'
                              }}>
                                {isTestOrder ? 'Test' : paymentType}
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                      <td>
                        {isUpdatingStatus === order.id ? (
                          <div className="status-updating">
                            <FontAwesomeIcon icon={faSpinner} spin /> Updating...
                          </div>
                        ) : (
                          <select 
                            className={`status-select ${getStatusClass(order.status)}`}
                            value={order.status || 'confirmed'}
                            onChange={(e) => handleStatusChange(order.id, e.target.value)}
                          >
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="processing">Processing</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        )}
                      </td>
                      <td className="actions-cell">
                        <button
                          className={`icon-button action-menu-trigger ${actionMenuOrder?.id === order.id ? 'active' : ''}`}
                          title="Actions"
                          aria-label="Order actions"
                          aria-haspopup="true"
                          aria-expanded={actionMenuOrder?.id === order.id}
                          onClick={(e) => toggleActionMenu(e, order)}
                        >
                          <FontAwesomeIcon icon={faEllipsisV} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={selectMode ? 10 : 9} className="no-data">
                      {error ? error : 'No orders found matching your filters.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {filteredOrders.length > ordersPerPage && (
            <div className="pagination-controls">
              <div className="pagination-info">
                Showing {Math.min((currentPage - 1) * ordersPerPage + 1, filteredOrders.length)} to {Math.min(currentPage * ordersPerPage, filteredOrders.length)} of {filteredOrders.length} orders
              </div>
              <div className="pagination-buttons">
                <button 
                  className="pagination-button" 
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </button>
                <button 
                  className="pagination-button" 
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                
                {/* Numbered page buttons */}
                <div className="page-numbers">
                  {generatePageNumbers().map((page, index) => (
                    page === '...' ? (
                      <span key={`ellipsis-${index}`} className="page-indicator">...</span>
                    ) : (
                      <button
                        key={page}
                        className={`pagination-button ${page === currentPage ? 'active' : ''}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    )
                  ))}
                </div>
                
                <button 
                  className="pagination-button" 
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
                <button 
                  className="pagination-button" 
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </button>
              </div>
            </div>
          )}
          
          {loading && orders.length > 0 && (
            <div className="loading-more">
              <FontAwesomeIcon icon={faSpinner} spin /> Loading more orders...
            </div>
          )}
          
          {filteredOrders.length === 0 && orders.length > 0 && (
            <div className="empty-state">
              <p>No orders found matching your search criteria.</p>
            </div>
          )}
          
          {orders.length === 0 && !loading && (
            <div className="empty-state">
              <p>No orders have been placed yet.</p>
            </div>
          )}
          
          {showManualOrderForm && (
            <ManualOrderForm
              order={editingOrder}
              onClose={handleCloseManualOrderForm}
              onSave={handleSaveManualOrder}
            />
          )}
        </>
      )}

      {/* Per-row actions menu (fixed-positioned so the table overflow can't clip it) */}
      {actionMenuOrder && (
        <div
          className="action-menu-dropdown"
          role="menu"
          style={{ position: 'fixed', top: actionMenuPos.top, right: actionMenuPos.right }}
        >
          <button
            className="action-menu-item"
            role="menuitem"
            onClick={() => { handleEditOrder(actionMenuOrder); setActionMenuOrder(null); }}
          >
            <FontAwesomeIcon icon={faEdit} fixedWidth /> Edit Order
          </button>
          <button
            className="action-menu-item"
            role="menuitem"
            onClick={() => { handleViewOrder(actionMenuOrder); setActionMenuOrder(null); }}
          >
            <FontAwesomeIcon icon={faEye} fixedWidth /> View Details
          </button>
          <button
            className="action-menu-item"
            role="menuitem"
            onClick={() => { handleGenerateInvoice(actionMenuOrder); setActionMenuOrder(null); }}
          >
            <FontAwesomeIcon icon={faPrint} fixedWidth /> Generate Invoice
          </button>
          <button
            className="action-menu-item"
            role="menuitem"
            disabled={isUpdatingStatus === actionMenuOrder.id}
            onClick={() => { handleArchiveOrder(actionMenuOrder.id, !actionMenuOrder.archived); setActionMenuOrder(null); }}
          >
            <FontAwesomeIcon icon={faArchive} fixedWidth /> {actionMenuOrder.archived ? 'Unarchive Order' : 'Archive Order'}
          </button>
          <div className="action-menu-divider" />
          <button
            className="action-menu-item danger"
            role="menuitem"
            disabled={loading}
            onClick={() => { handleDeleteOrder(actionMenuOrder.id, actionMenuOrder.orderNumber); setActionMenuOrder(null); }}
          >
            <FontAwesomeIcon icon={faTrash} fixedWidth /> Delete Order
          </button>
        </div>
      )}
    </div>
  );
};

export default OrdersSection;
